import { mixed } from "yup"
import {
  buildDateISOString,
  isValidISOString,
  type DateISOString,
  type FourDigitString,
  type TwoDigitString,
} from "../../util/date-iso-string"

const PAD = 2

const isDateISOString = {
  name: "validISOString",
  message: "yup.common.date.invalidDate",
  test: (date?: string) => {
    if (!date) return true // Let required() handle if needed
    return isValidISOString(date)
  },
}

export function dateISOString() {
  return mixed<DateISOString>().test(isDateISOString)
}

interface GetTodayAsLocalISOStringOptions {
  yearOffset: number
}

export function getTodayAsLocalISOString(
  options: GetTodayAsLocalISOStringOptions = {
    yearOffset: 0,
  }
) {
  const { yearOffset } = options

  const today = new Date()
  return buildDateISOString({
    year: String(today.getFullYear() + yearOffset) as FourDigitString,
    month: String(today.getMonth() + 1).padStart(PAD, "0") as TwoDigitString,
    day: String(today.getDate()).padStart(PAD, "0") as TwoDigitString,
  }) as DateISOString
}

export function isFutureDate(message?: string) {
  return {
    name: "isFutureDate",
    message: message || "yup.common.date.futureDateRequired",
    test: (date?: string) => {
      if (!date) return true
      return date > getTodayAsLocalISOString()
    },
  }
}

interface IsPastDateOptions {
  allowToday?: boolean
}

export function isPastDate(
  options: IsPastDateOptions = {
    allowToday: false,
  },
  message?: string
) {
  return {
    name: "isPastDate",
    message: message || "yup.common.date.pastDateRequired",
    test: (date?: string) => {
      if (!date) return true
      return options.allowToday
        ? date <= getTodayAsLocalISOString()
        : date < getTodayAsLocalISOString()
    },
  }
}

export function isWithinMaxYears(years: number, message?: string) {
  return {
    name: "isWithinMaxYears",
    message: message || {
      key: "yup.common.date.beyondMaxYears",
      values: { maxYears: years },
    },
    test: (date?: string) => {
      if (!date) return true
      return date < getTodayAsLocalISOString({ yearOffset: years })
    },
  }
}
